import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import CustomerOpportunitiesSection from "@/components/customers/CustomerOpportunitiesSection";
import SectionCard from "@/components/customers/SectionCard";
import NewOpportunityDialog from "@/components/salgsmuligheter/NewOpportunityDialog";
import { useOffers, type OfferWithCustomer } from "@/hooks/use-offers-queries";
import { FALLBACK_WEIGHTS, useOfferPipelineWeights } from "@/hooks/use-offer-pipeline";
import { useCustomerOpportunities } from "@/hooks/use-opportunities";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { daysUntil } from "@/lib/customer-kpi-calc";
import { formatDate, formatDateShort, formatNOK } from "@/lib/format";
import { bucketFor, type ProbabilityWeights } from "@/lib/offer-pipeline-calc";
import { openProcessesWithoutOffer } from "@/lib/opportunity-calc";
import { TONE_TEXT } from "@/lib/widget-status";

interface CustomerOpenOffersSectionProps {
  customerId: string;
  customerName: string;
}

function closeText(date: string | null, today: Date): { text: string; className: string } {
  if (!date) return { text: "Close ikke satt", className: "text-muted-foreground" };
  const days = daysUntil(date, today);
  const when = days === 0 ? "i dag" : days > 0 ? `om ${days} ${days === 1 ? "dag" : "dager"}` : `for ${-days} ${-days === 1 ? "dag" : "dager"} siden`;
  return { text: `Close ${formatDateShort(date)}, ${when}`, className: days < 0 ? TONE_TEXT.miss : "" };
}

function OfferCard({ offer, weights, pipelineOn, today }: { offer: OfferWithCustomer; weights: ProbabilityWeights; pipelineOn: boolean; today: Date }) {
  const expired = bucketFor(offer, today) === "expired";
  const chance = offer.probability ? Math.round(weights[offer.probability] * 100) : null;
  const close = closeText(offer.expected_close_date, today);
  return (
    <li className="space-y-3 border border-border p-4" aria-label={`Tilbud: ${offer.title}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/tilbud/${offer.id}`} className="font-medium underline-offset-4 hover:underline">
            {offer.title}
          </Link>
          <div className="text-xs text-muted-foreground">
            {offer.creator?.full_name ?? "Ukjent"}
            {offer.sent_at ? ` · sendt ${formatDate(offer.sent_at)}` : ""}
          </div>
        </div>
        <StatusBadge tone={expired ? "miss" : "near"} label={expired ? "Utløpt" : "Sendt"} />
      </div>
      <dl className={`grid gap-3 text-sm ${pipelineOn ? "sm:grid-cols-3" : "sm:grid-cols-1"}`}>
        <div>
          <dt className="text-xs text-muted-foreground">Beløp</dt>
          <dd className="font-medium tabular-nums">{formatNOK(offer.total_amount)}</dd>
        </div>
        {pipelineOn && (
          <>
            <div>
              <dt className="text-xs text-muted-foreground">Sjanse</dt>
              <dd className="space-y-1">
                <span className="font-medium tabular-nums">{chance != null ? `${chance} %` : "Ikke satt"}</span>
                <div className="h-1.5 w-full bg-muted" aria-hidden="true">
                  <div className="h-full bg-primary" style={{ width: `${chance ?? 0}%` }} />
                </div>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Close</dt>
              <dd className={`font-medium ${close.className}`}>{close.text.replace(/^Close /, "")}</dd>
            </div>
          </>
        )}
      </dl>
    </li>
  );
}

/**
 * «Tilbud ute»: kundens sendte tilbud som kort med beløp, sjanse og close-dato,
 * utkast som liste under, og (bak feature_crm_ko_pipe) prosessene som ennå ikke
 * har tilbud. «Ny prosess» ligger i topplinjen, og uten tilbud og prosesser er
 * kortet én linje. Tilbudsstatus og sannsynlighet kommer fra tilbudet selv;
 * vektene er de samme som i Pipe.
 */
export default function CustomerOpenOffersSection({ customerId, customerName }: CustomerOpenOffersSectionProps) {
  const { data: offers = [], isLoading } = useOffers();
  const { data: weights = FALLBACK_WEIGHTS } = useOfferPipelineWeights();
  const pipelineOn = useFeatureFlag("feature_offer_pipeline");
  const koPipe = useFeatureFlag("feature_crm_ko_pipe");
  const { data: opportunities = [] } = useCustomerOpportunities(customerId, { enabled: koPipe });
  const [newProcessOpen, setNewProcessOpen] = useState(false);
  const today = new Date();

  const mine = useMemo(() => offers.filter((o) => o.customer_id === customerId), [offers, customerId]);
  const out = mine.filter((o) => o.status === "sent");
  const drafts = mine.filter((o) => o.status === "draft");
  const outKr = out.reduce((s, o) => s + o.total_amount, 0);
  const processes = koPipe ? openProcessesWithoutOffer(opportunities).length : 0;
  const hasContent = out.length > 0 || drafts.length > 0 || processes > 0;

  return (
    <>
      <SectionCard
        title="Tilbud ute"
        meta={out.length > 0 ? `${out.length} tilbud · ${formatNOK(outKr)}` : undefined}
        empty={koPipe
          ? "Ingen tilbud ute. «Nytt tilbud» øverst lager et, «Ny prosess» starter en outreach."
          : "Ingen tilbud ute. «Nytt tilbud» øverst lager et med kunden valgt."}
        actions={
          <>
            <Link to="/tilbud?mine=0" className="text-sm text-primary hover:underline">
              Alle tilbud, {mine.length}
            </Link>
            {koPipe && (
              <Button variant="outline" size="sm" onClick={() => setNewProcessOpen(true)}>
                Ny prosess
              </Button>
            )}
          </>
        }
      >
        {isLoading ? (
          <Skeleton className="m-5 h-24" />
        ) : !hasContent ? null : (
          <>
            {(out.length > 0 || drafts.length > 0) && (
              <div className="space-y-3 px-5 py-4">
                {out.length > 0 && (
                  <ul className="grid gap-3 md:grid-cols-2">
                    {out.map((o) => (
                      <OfferCard key={o.id} offer={o} weights={weights} pipelineOn={pipelineOn} today={today} />
                    ))}
                  </ul>
                )}
                {drafts.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {drafts.length} utkast:{" "}
                    {drafts.map((d, i) => (
                      <span key={d.id}>
                        {i > 0 ? ", " : ""}
                        <Link to={`/tilbud/${d.id}`} className="text-primary hover:underline">{d.title}</Link>
                      </span>
                    ))}
                  </p>
                )}
              </div>
            )}
            {koPipe && <CustomerOpportunitiesSection customerId={customerId} customerName={customerName} embedded />}
          </>
        )}
      </SectionCard>

      {koPipe && (
        <NewOpportunityDialog open={newProcessOpen} onOpenChange={setNewProcessOpen} customerId={customerId} customerName={customerName} />
      )}
    </>
  );
}
